import { useMemo, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { Button, Modal, Form, Radio, InputNumber, Input, message, Tooltip } from "antd";
import { CalendarOutlined, ThunderboltOutlined, DollarOutlined, EyeOutlined, TeamOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useGetTrainerIncentiveHistoryQuery, usePayTrainerIncentiveMutation } from "../../../services/trainer";
import "./styles.scss";

/* ─── Helpers ─────────────────────────────────────────────────────────── */

const inr = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// The backend's `status` string varies ("unpaid", "partialpaid", "paid" …) —
// normalize it to a fixed key so the CSS classes / Pay-button gating stay stable.
const statusKey = (status: string) => {
  const v = (status || '').toLowerCase();
  if (v.includes('partial')) return 'partial';
  if (v.includes('unpaid')) return 'unpaid';
  return 'paid';
};

const statusLabel = (status: string) => {
  const key = statusKey(status);
  return key === 'partial' ? 'Partial Paid' : key === 'unpaid' ? 'Unpaid' : 'Paid';
};

/* ─── Component ───────────────────────────────────────────────────────── */

const IncentiveHistory = () => {
  const { id } = useParams<{ id: string }>();
  const { trainer } = useOutletContext<{ trainer: any }>() ?? { trainer: {} };
  const trainerId: string = (id as string) || trainer?._id;

  const [open, setOpen] = useState(false);

  const onClose = () => {
    setOpen(false);
  };

  // GET all past generated incentive payouts for this trainer.
  const { data: histResp, isFetching: loading } = useGetTrainerIncentiveHistoryQuery(
    { trainerId },
    { skip: !trainerId },
  );
  const [payTrainerIncentive, { isLoading: paying }] = usePayTrainerIncentiveMutation();

  const history = useMemo(() => {
    const raw = (histResp as any)?.data ?? histResp;
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw : [raw];
    return list.map((x: any, i: number) => ({
      id:               x._id ?? x.id ?? `hist-${i}`,
      fromDate:         x.fromDate,
      toDate:           x.toDate,
      noOfSessions:     Number(x.noOfSessions ?? x.totalSessions ?? 0),
      revenueGenerated: Number(x.revenueGenerated ?? x.revenue ?? 0),
      totalClients:     Number(x.totalClients ?? 0),
      slabPercentage:   Number(x.slabPercentage ?? 0),
      incentiveAmount:  Number(x.incentiveAmount ?? 0),
      tdsPercent:       Number(x.tdsPercent ?? 0),
      tdsAmount:        Number(x.tdsAmount ?? 0),
      gstPercent:       Number(x.gstPercent ?? 0),
      gstAmount:        Number(x.gstAmount ?? 0),
      totalAmount:      Number(x.totalAmount ?? 0),
      additionalFields: Array.isArray(x.additionalFields)
        ? x.additionalFields.map((f: any) => ({
            fieldName: f.fieldName ?? '',
            amount:    Number(f.amount ?? 0),
            remark:    f.remark ?? '',
          }))
        : [],
      finalTotal:       Number(x.finalTotal ?? x.totalAmount ?? 0),
      deduction:        Number(x.deduction ?? 0),
      deductionRemark:  x.deductionRemark ?? '',
      remark:           x.remark ?? '',
      paidAmount:       Number(x.paidAmount ?? 0),
      dueAmount:        Number(x.dueAmount ?? (Number(x.finalTotal ?? x.totalAmount ?? 0) - Number(x.paidAmount ?? 0))),
      paymentStatus:    (x.status ?? x.paymentStatus ?? 'unpaid') as string,
      payments: Array.isArray(x.payments)
        ? x.payments.map((p: any) => ({
            amount:      Number(p.amount ?? 0),
            paymentType: p.paymentType ?? '',
            note:        p.note ?? '',
            paidAt:      p.paidAt,
          }))
        : [],
      generatedAt:      x.generatedAt ?? x.createdAt,
      generatedBy:      x.generatedBy?.name ?? x.createdBy?.name ?? '',
    }));
  }, [histResp]);

  // ── Pay modal state ──
  const [payEntry, setPayEntry] = useState<(typeof history)[number] | null>(null);
  const [paymentMode, setPaymentMode] = useState<'partial' | 'full'>('full');
  const [paymentAmount, setPaymentAmount] = useState<number | null>(0);
  const [paymentNote, setPaymentNote] = useState('');
  const [deduction, setDeduction] = useState<number | null>(0);
  const [deductionRemark, setDeductionRemark] = useState('');

  const openPayModal = (entry: (typeof history)[number]) => {
    setPayEntry(entry);
    setPaymentMode('full');
    setPaymentAmount(Math.max(0, entry.dueAmount));
    setPaymentNote('');
    setDeduction(0);
    setDeductionRemark('');
  };

  const closePayModal = () => setPayEntry(null);

  const handlePaySubmit = async () => {
    if (!payEntry) return;
    const due = Math.max(0, payEntry.dueAmount);
    const amount = paymentMode === 'full' ? due : Math.min(Number(paymentAmount) || 0, due);
    if (amount <= 0) { message.error('Enter a valid amount'); return; }

    try {
      await payTrainerIncentive({
        trainerId,
        incentiveId: payEntry.id,
        paymentType: paymentMode,
        amount,
        note: paymentNote,
        ...(deduction ? { deduction, deductionRemark } : {}),
      }).unwrap();
      closePayModal();
    } catch {
      /* error surfaced by the global bad-request handler */
    }
  };

  return (
    <>
      <div className="incentive-history">
        <div className="ih-head">
          <div>
            <h3 className="ih-title">Incentive History</h3>
            <p className="ih-sub">
              Past incentive payouts &amp; slab history for this trainer
            </p>
          </div>

          <div className="ih-head-actions">
            {/* <Button
              type="primary"
              className="ic-add-btn"
              onClick={() => setOpen(true)}
            >
              Generate Incentive
            </Button> */}
          </div>
        </div>

        {loading ? (
          <div className="ih-empty">Loading…</div>
        ) : history.length === 0 ? (
          <div className="ih-empty">Incentive history will appear here.</div>
        ) : (
          <div className="ih-cards">
            {history.map((entry) => {
              const skey = statusKey(entry.paymentStatus);
              return (
                <div className={`ih-card ih-card-${skey}`} key={entry.id}>
                  <div className="ih-card-top">
                    <div className="ih-card-head">
                      <span className="ih-card-period">
                        <CalendarOutlined />
                        {entry.fromDate ? dayjs(entry.fromDate).format('DD MMM YYYY') : '-'}
                        {' – '}
                        {entry.toDate ? dayjs(entry.toDate).format('DD MMM YYYY') : '-'}
                        {entry.remark && (
                          <Tooltip title={entry.remark}>
                            <EyeOutlined className="ih-remark-icon" />
                          </Tooltip>
                        )}
                      </span>
                      {entry.generatedAt && (
                        <span className="ih-card-date">
                          Generated {dayjs(entry.generatedAt).format('DD MMM YYYY, hh:mm A')}
                          {entry.generatedBy && ` · ${entry.generatedBy}`}
                        </span>
                      )}
                    </div>
                    <div className="ih-card-top-right">
                      <span className={`ih-pay-status ih-pay-status-${skey}`}>
                        {statusLabel(entry.paymentStatus)}
                      </span>
                      {skey !== 'paid' && entry.dueAmount > 0 && (
                        <Button size="small" type="primary" className="ih-pay-btn" onClick={() => openPayModal(entry)}>
                          Pay
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="ih-card-stats">
                    <div className="ih-stat">
                      <span className="ih-stat-icon"><ThunderboltOutlined /></span>
                      <div>
                        <div className="ih-stat-label">Sessions</div>
                        <div className="ih-stat-value">{entry.noOfSessions}</div>
                      </div>
                    </div>
                    <div className="ih-stat">
                      <span className="ih-stat-icon"><DollarOutlined /></span>
                      <div>
                        <div className="ih-stat-label">Revenue</div>
                        <div className="ih-stat-value">{inr(entry.revenueGenerated)}</div>
                      </div>
                    </div>
                    <div className="ih-stat">
                      <span className="ih-stat-icon">%</span>
                      <div>
                        <div className="ih-stat-label">Slab</div>
                        <div className="ih-stat-value">{entry.slabPercentage}%</div>
                      </div>
                    </div>
                    <div className="ih-stat ih-stat-highlight">
                      <span className="ih-stat-icon"><TeamOutlined /></span>
                      <div>
                        <div className="ih-stat-label">Total Clients</div>
                        <div className="ih-stat-value">{entry.totalClients}</div>
                      </div>
                    </div>
                  </div>

                  <div className="ih-card-breakup">
                    <div className="ih-row">
                      <span>Incentive Amount</span>
                      <span>{inr(entry.incentiveAmount)}</span>
                    </div>
                    <div className="ih-row">
                      <span>TDS ({entry.tdsPercent}%)</span>
                      <span className="ih-neg">− {inr(entry.tdsAmount)}</span>
                    </div>
                    <div className="ih-row">
                      <span>GST ({entry.gstPercent}%)</span>
                      <span className="ih-neg">− {inr(entry.gstAmount)}</span>
                    </div>
                    <div className="ih-row ih-total">
                      <span>Total Payable</span>
                      <span>{inr(entry.totalAmount)}</span>
                    </div>

                    {entry.additionalFields.length > 0 && (
                      <div className="ih-additional-fields">
                        {entry.additionalFields.map((f: any, idx: number) => (
                          <div className="ih-row" key={idx}>
                            <span>
                              {f.fieldName}
                              {f.remark && (
                                <Tooltip title={f.remark}>
                                  <EyeOutlined className="ih-remark-icon" />
                                </Tooltip>
                              )}
                            </span>
                            <span>{inr(f.amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {entry.deduction > 0 && (
                      <div className="ih-row">
                        <span>
                          Deduction
                          {entry.deductionRemark && (
                            <Tooltip title={entry.deductionRemark}>
                              <EyeOutlined className="ih-remark-icon" />
                            </Tooltip>
                          )}
                        </span>
                        <span className="ih-neg">− {inr(entry.deduction)}</span>
                      </div>
                    )}

                    <div className="ih-row ih-total">
                      <span>Net Payable</span>
                      <span>{inr(entry.finalTotal)}</span>
                    </div>
                    {entry.payments.length > 0 && (
                      <div className="ih-payment-list">
                        {entry.payments.map((p: any, idx: number) => (
                          <div className="ih-payment-row" key={idx}>
                            <span className="ih-payment-left">
                              <span className={`ih-payment-type ih-payment-type-${(p.paymentType || '').toLowerCase()}`}>
                                {p.paymentType || '-'}
                              </span>
                              {p.paidAt && <span className="ih-payment-date">{dayjs(p.paidAt).format('DD MMM, hh:mm A')}</span>}
                              {p.note && (
                                <Tooltip title={p.note}>
                                  <EyeOutlined className="ih-remark-icon" />
                                </Tooltip>
                              )}
                            </span>
                            <span className="ih-payment-amount">{inr(p.amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {entry.paidAmount > 0 && (
                      <div className="ih-row">
                        <span>Paid</span>
                        <span>{inr(entry.paidAmount)}</span>
                      </div>
                    )}
                    {skey !== 'paid' && (
                      <div className="ih-row">
                        <span>Due</span>
                        <span>{inr(entry.dueAmount)}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Pay modal ── */}
      <Modal
        title="Record Payment"
        open={!!payEntry}
        onCancel={closePayModal}
        onOk={handlePaySubmit}
        okText="Pay"
        confirmLoading={paying}
        destroyOnHidden
      >
        {payEntry && (
          <Form layout="vertical">
            <Form.Item label="Payment Mode">
              <Radio.Group
                value={paymentMode}
                onChange={(e) => {
                  const mode = e.target.value;
                  setPaymentMode(mode);
                  setPaymentAmount(mode === 'full' ? Math.max(0, payEntry.dueAmount) : null);
                }}
              >
                <Radio.Button value="full">Fully Paid</Radio.Button>
                <Radio.Button value="partial">Partial</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item label="Amount">
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={Math.max(0, payEntry.dueAmount)}
                value={paymentAmount}
                placeholder={paymentMode === 'partial' ? 'Enter amount' : undefined}
                disabled={paymentMode === 'full'}
                onChange={(v) => setPaymentAmount(v === null ? null : Number(v) || 0)}
              />
            </Form.Item>
            <Form.Item label="Note">
              <Input.TextArea
                rows={2}
                placeholder="e.g. 1st installment"
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
              />
            </Form.Item>
            <Form.Item label="Deduction (optional)">
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                placeholder="e.g. advance already paid"
                value={deduction}
                onChange={(v) => setDeduction(v === null ? null : Number(v) || 0)}
              />
            </Form.Item>
            {!!deduction && (
              <Form.Item label="Deduction Remark">
                <Input.TextArea
                  rows={2}
                  placeholder="e.g. Advance already diya tha, wahi adjust kiya"
                  value={deductionRemark}
                  onChange={(e) => setDeductionRemark(e.target.value)}
                />
              </Form.Item>
            )}
          </Form>
        )}
      </Modal>

      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={560}
        centered
        destroyOnHidden
        title={"Select Date to Generate Incentive"}
      >
        <>

        </>
      </Modal>
    </>
  );
};

export default IncentiveHistory;
